import React, { Fragment, useEffect, useState, useRef, Suspense, LegacyRef, RefObject, useCallback } from 'react';
import {
    Wrapper,
    LogoWrapper,
    TextTitle,
    MenuItemsWrapper,
    MenuItemWrapper,
    MenuItemIcon,
    MenuItemText,
    FlexSpace,
    CreateMenuButtonIcon,
    UserMenuWrapper,
    UserMenuText,
    UserMenuUserName,
    UserMenuIcon,
    PremiumIcon,
    Logo,
    LinkedMenuItemWrapper,
    NotificationsWrapper,
    LinkedLogoWrapper,
    NotificationsSubstrate,
    ArrowRightIcon,
    ItemMenuWrapper,
    ItemMenuButton,
} from './Header.style';
import { PopperBase } from '@toluna-ui-toolkit/popper-base';
import { UserMenu } from './user-menu/UserMenu';
import {
    getInitialData,
    clientSwitchContext,
    logoutAndRedirect,
    getSolutionsData,
} from '../../common/services/api.service';
import { Theme } from '../../common/theme/Theme';
import contactUsModalEventDispatcher from '../../common/events/contactUsModal.events';
import headerEventDispatcher from '../../common/events/header.events';
import { FormType } from '../../contactUs/data/constants';
import { useEvent } from '../../common/hooks/useEvent.hook';
import { useWebsocket } from '../../common/hooks/useWebsocket.hook';
import { useLatestRef } from '../../common/hooks/useLatestRef.hook';
import { headerEvents } from '../../common/events/header.events';
import { BreadcrumbsEvents } from '../../common/events/breadcrumbs.events';
import NotificationsManager from '../../notifications/components/NotificationsManager';
import { createSolutionRoute, checkIsExternalLink, convertToWwwUrl } from '../../../utils/url.service';
import accountNotificationsService from '../../accountNotifications/data/accountNotifications.service';
import { DelayedFallback } from '../../common/components/DelayedSuspenseFallback/DelayedSuspenseFallback';
import { SuspendedCreateMenu } from './create-menu/components/CreateMenu.suspended';
import { Translations } from '../../common/models/common.models';
import { useData } from 'shell/dataContext';
import { createWsUrl, getIsImpersonationWsValid, validateAuthHeader } from '../../common/services/utils';
import { setWalkMeTemplateId } from '../../walkme/walkme.module';
import { Notification } from '../../notifications/data/models';
const CreateMenu = React.lazy(() => import(/* webpackChunkName: "CreateMenu" */ './create-menu/components/CreateMenu'));
import BetaSuggestionModal from '../../betaSuggestion/components/BetaSuggestion.modal';
import ImportProject from '../../importProject/components/ImportProject';
import { ELicenseStatus, externalUrls } from '../../../constants';
import { Solution, SolutionsData } from '../header.models';
import { useLocation } from 'react-router-dom';
import { HeaderSkeleton } from './HeaderSkeleton';

export interface IHeaderProps {
    title: string;
    children: JSX.Element;
    baseUrl: string;
    cookieDomain: string;
    onLoad: () => void;
    onLoadError: (error: string) => void;
    isNewBrand: boolean;
    onSetIsNewBrand: (state: boolean) => void;
    disableScrollBehaviour: boolean;
    setTcpEnabled: (state: boolean) => void;
    id: string;
    fixedNotification?: boolean;
}

type HeaderData = {
    createMenu: any;
    headerLinks: { icon: string; linkTo: string; text: string }[];
    logoUrl: string;
    notifications: [];
    tiuiDomain: string;
    translations: Translations;
    user: any;
    additionalFiles: string[];
};

type SocketData = {
    socketUrl: string;
    redirectUrl: string;
    customerId: string;
};

type PopperInstance = {
    toggle: () => void;
    recalculatePosition: () => void;
    isOpen: boolean;
};

function checkIsAllowedToScroll(condition: boolean) {
    return condition && !document.querySelector('.ti-breadcrumbs');
}

export const Header = (props: IHeaderProps) => {
    const {
        title,
        children,
        baseUrl,
        cookieDomain,
        fixedNotification,
        onLoad,
        onLoadError,
        isNewBrand,
        onSetIsNewBrand,
        disableScrollBehaviour,
        setTcpEnabled,
    } = props;

    const [isOnTop, setIsOnTop] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);
    const [showCreateMenu, setShowCreateMenu] = useState(false);
    const [headerData, setHeaderData] = useState<HeaderData>({} as HeaderData);
    const userPopperRef = useRef<PopperInstance | null>(null);
    const [currentIsNewBrand] = useState(isNewBrand);
    const [isHasNotifications, setIsHasNotifications] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [accountNotifications, setAccountNotifications] = useState([]);
    const [socketData, setSocketData] = useState<SocketData>({} as SocketData);
    const notificationWrapperRef = useRef<LegacyRef<HTMLDivElement>>();
    const {
        redirectToLogin,
        isHosted,
        redirectToUrl,
        setSolutionsData: setShellSolutionsData,
        history,
        setUserData,
    } = useData() ?? {};
    const [isAllowedToScroll, setAllowedToScroll] = useState(checkIsAllowedToScroll(!disableScrollBehaviour));
    const isAllowedToScrollRef = useLatestRef(isAllowedToScroll);
    const hasBreadcrumbs = Boolean(document.querySelector('.ti-breadcrumbs'));
    const [solutionsData, setSolutionsData] = useState<SolutionsData>({} as SolutionsData);
    const location = useLocation();
    let portfolioScrollTimer: number | NodeJS.Timeout | undefined;

    useEffect(() => {
        const initSolutionsData = async () => {
            getSolutionsData(
                window.ts.globals.QUESTIONNAIRE_GW_BASE_URL,
                data => {
                    setSolutionsData(data);
                    setShellSolutionsData?.(data);
                },
                () => {}
            );
        };
        initSolutionsData();
    }, []);

    useEffect(() => {
        if (location.hash === '#solution-portfolio') {
            portfolioScrollTimer = setInterval(() => {
                const portfolioElement = document.getElementById('solution-portfolio');
                if (portfolioElement) {
                    portfolioElement.scrollIntoView({ behavior: 'smooth' });
                    clearInterval(portfolioScrollTimer);
                }
            }, 100);
        }
        return () => {
            clearInterval(portfolioScrollTimer as number);
        };
    }, [location]);

    let throttleCached: Event | null = null;

    const { IsSXTIDefineEnabled: isSXTIDefineEnabled, IsMergeImportFromUPSEnabled: isMergeImportFromUPSEnabled } =
        headerData?.user?.userDetailsTraits?.traits ?? {};

    const showImportButton = isSXTIDefineEnabled && isMergeImportFromUPSEnabled;

    const setTSDomain = (domain: string) => {
        if (window.tcp) {
            window.tcp.tsDomain = domain;
        }
    };

    const handleScroll = (evt: Event) => {
        if (!throttleCached) {
            setTimeout(() => {
                if (
                    window.scrollY + (isOnTop ? 0 : 32) >
                    48 + (notificationWrapperRef as RefObject<any>)?.current?.clientHeight
                ) {
                    if (isOnTop) {
                        setIsOnTop(false);
                        headerEventDispatcher.notifyHeaderMinimized(true);
                    }
                } else if (!isOnTop) {
                    setIsOnTop(true);
                    headerEventDispatcher.notifyHeaderMinimized(false);
                }
                throttleCached = null;
            }, 100);
        }
        throttleCached = evt;
    };

    function handleImpersonationRedirect(data: any) {
        const { customerId, redirectUrl } = socketData;
        const json = JSON.parse(data);
        try {
            if (json.ClientId) {
                const { ClientId } = json;
                if (ClientId !== customerId) {
                    window.location.href = redirectUrl;
                }
            }
        } catch (err) {
            console.log('on Message error', err); //eslint-disable-line
        }
    }

    const handleRedirectToUrl = async (redirectLink: string) => {
        if (!isHosted) {
            window.location.href = `${window.ts.globals.LEGACY_SHELL_URL}${redirectLink}`;
            return;
        }

        redirectToUrl?.(redirectLink);
    };

    const handleOpenImportModal = () => {
        document.body.style.overflow = 'hidden';
        setShowImportModal(true);
    };

    const clickSolutionItemHandler = (item: Partial<Solution>) => {
        setWalkMeTemplateId(item.id);
        if (showCreateMenu) onHandleOpenCloseCreateMenu();

        if (item.linkTo) {
            handleRedirectToUrl(item.linkTo);
        }
    };

    const transformHeaderData = (data: HeaderData): HeaderData => {
        return {
            ...data,
            tiuiDomain: convertToWwwUrl(data.tiuiDomain),
        };
    };

    useEffect(() => {
        if (!hasBreadcrumbs && !disableScrollBehaviour) {
            window.addEventListener('scroll', handleScroll, { passive: true });
            if (userPopperRef.current && userPopperRef.current?.isOpen) {
                for (let i = 0; i < 6; i++) {
                    setTimeout(() => {
                        userPopperRef.current?.recalculatePosition();
                    }, 50 * i);
                }
            }
            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        }
    }, [isOnTop, isAllowedToScroll, hasBreadcrumbs]);

    useEffect(() => {
        function handleCheckIsAllowedToScroll() {
            const isAllowed = checkIsAllowedToScroll(!disableScrollBehaviour);

            if (isAllowed !== isAllowedToScrollRef?.current) {
                setAllowedToScroll(isAllowed);
            }
        }

        handleCheckIsAllowedToScroll();
        window.addEventListener(BreadcrumbsEvents.initialize, handleCheckIsAllowedToScroll);
        return () => {
            window.removeEventListener(BreadcrumbsEvents.initialize, handleCheckIsAllowedToScroll);
        };
    }, [disableScrollBehaviour]);

    const handleOpenCloseCreateMenu = useCallback((show?: boolean) => {
        if (typeof show === 'boolean') {
            setShowCreateMenu(show);
        } else {
            setShowCreateMenu(s => !s);
        }
    }, []);

    useEvent({
        name: headerEvents.HIDE_CREATE_MENU,
        handler: () => handleOpenCloseCreateMenu(false) as unknown as void,
    });

    useWebsocket(socketData, getIsImpersonationWsValid(socketData), handleImpersonationRedirect);

    useEffect(() => {
        if (accountNotifications && headerData.translations) {
            accountNotificationsService.handleNewNotifications(accountNotifications);
        }
    }, [accountNotifications, headerData.translations]);

    const initComponent = () => {
        getInitialData(
            baseUrl,
            response => {
                if (response) {
                    validateAuthHeader();
                    setHeaderData(transformHeaderData(response));
                    accountNotificationsService.initService(response, cookieDomain);
                    setTSDomain(convertToWwwUrl(response.tiuiDomain));
                    setIsInitialized(true);
                    const userId = response.user.userId;
                    if (userId) {
                        window.gtag &&
                            window.gtag('config', 'UA-52488016-6', {
                                user_id: userId,
                                dimension1: userId,
                            });
                    }
                    if (response.additionalFiles && response.additionalFiles.length) {
                        response.additionalFiles.forEach((file: string) => {
                            var link = document.createElement('link');
                            link.href = file;
                            link.type = 'text/css';
                            link.rel = 'stylesheet';
                            document.getElementsByTagName('head')[0].appendChild(link);
                        });
                    }
                    if (response.notifications && response.notifications.length)
                        setAccountNotifications(response.notifications);
                    onLoad?.();
                    setSocketData({
                        socketUrl: response?.subEthaUrl && createWsUrl(response?.subEthaUrl, ['AuthToken', 'User']),
                        redirectUrl: response?.tsiDashboardUrl,
                        customerId: response?.user?.customerId,
                    });
                    setUserData?.(response.user);
                }
            },
            (error: any) => {
                onLoadError && onLoadError(error);
            }
        );
    };

    const handleSignOut = async () => {
        onSetIsNewBrand && onSetIsNewBrand(currentIsNewBrand);

        await logoutAndRedirect({ baseUrl, cookieDomain }, () => {
            if (setTcpEnabled && redirectToLogin) {
                //On log out, destroy TCP to show guest header and footer
                setTcpEnabled(false);
                redirectToLogin(false);
            } else {
                window.location.href = window.ts.globals.LOGIN_URL;
            }
        });
    };

    const handleClientContextChange = () => {
        clientSwitchContext(
            baseUrl,
            response => {
                window.location.href = 'https://' + response.redirectUrl;
            },
            () => {}
        );
    };

    const onHandleOpenCloseCreateMenu = (show?: boolean) => {
        handleOpenCloseCreateMenu(show);
    };

    const showUpgradeLicenseContactUsModal = () => {
        contactUsModalEventDispatcher.showContactUsModal(FormType.ADVANCED_LICENSE);
    };

    const onAddNotifications = (newNotifications: Notification[]) => {
        setIsHasNotifications(newNotifications.length > 0);
    };

    const onCloseNotification = (notifications: Notification[]) => {
        setIsHasNotifications(notifications.length > 0);
    };

    const redirectToPricingPage = () => {
        window.location.href = `${headerData.tiuiDomain}pricing`;
    };

    const menuItemClick = () => {
        setShowCreateMenu(false);
    };

    useEffect(() => {
        if (baseUrl && !title && !children) {
            initComponent();
        }
    }, [baseUrl]);

    const onScrollToPortfolio = useCallback(async () => {
        handleOpenCloseCreateMenu();

        if (!isHosted) {
            window.location.href = `${window.ts.globals.LEGACY_SHELL_URL}/dashboard#solution-portfolio`;
            return;
        } else
            history.push({
                pathname: '/dashboard',
                hash: 'solution-portfolio',
            });

        setTimeout(() => {
            const section = document.getElementById('solution-portfolio');
            const focusable = section?.querySelector('button, [tabindex]:not([tabindex="-1"])') as HTMLElement;

            focusable?.focus();
        }, 100);
    }, [location]);

    const toggleMenu = () => {
        if (userPopperRef.current) {
            userPopperRef.current?.toggle();
        }
    };

    const handleKeyDownImport = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpenImportModal();
        }
    };

    const modalRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!window.tcp.createCustomSurvey) window.tcp.createCustomSurvey = {};
        window.tcp.createCustomSurvey.createCustomSurveyButtonRef = modalRef as RefObject<HTMLButtonElement>;
    }, [showCreateMenu]);

    const handleKeyDownCreate = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onHandleOpenCloseCreateMenu();
        }
    };

    const handleKeyDownUserMenu = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleMenu();
        }
    };

    const handleFocusParent = () => {
        const triggerElement = document.getElementById('userMenuTrigger');

        if (triggerElement) {
            triggerElement.focus();
        }
    };
    return (
        <Theme isNewBrand={currentIsNewBrand}>
            <Wrapper className={`ti-header-wrapper${isOnTop ? ' onTop' : ''}`} isontop={isOnTop} isInitialized={isInitialized}>
                {title || children ? (
                    children ? (
                        children
                    ) : (
                        <TextTitle isontop={isOnTop}>{title}</TextTitle>
                    )
                ) : isInitialized && headerData ? (
                    <Fragment>
                        {isHosted ? (
                            <LinkedLogoWrapper
                                onClick={menuItemClick}
                                isontop={isOnTop.toString()}
                                to={'/dashboard'}
                                tabIndex={0}
                            >
                                <Logo />
                            </LinkedLogoWrapper>
                        ) : (
                            <LogoWrapper
                                onClick={menuItemClick}
                                isontop={isOnTop.toString()}
                                href={`${headerData.tiuiDomain}dashboard`}
                            >
                                <Logo />
                            </LogoWrapper>
                        )}
                        <MenuItemsWrapper>
                            {headerData.headerLinks &&
                                headerData.headerLinks.map((item, index) => {
                                    if (
                                        item.linkTo.includes(externalUrls.PROJECT.toLowerCase()) &&
                                        headerData?.user?.userDetailsTraits?.traits?.IsSpaMyProjectsUI
                                    ) {
                                        if (isHosted)
                                            return (
                                                <LinkedMenuItemWrapper
                                                    tabIndex={0}
                                                    onClick={menuItemClick}
                                                    key={index}
                                                    to={'/projects'}
                                                >
                                                    <MenuItemIcon className={item.icon} />
                                                    <MenuItemText>{item.text}</MenuItemText>
                                                </LinkedMenuItemWrapper>
                                            );
                                        else
                                            return (
                                                <MenuItemWrapper onClick={menuItemClick} key={index} href={item.linkTo}>
                                                    <MenuItemIcon className={item.icon} />
                                                    <MenuItemText>{item.text}</MenuItemText>
                                                </MenuItemWrapper>
                                            );
                                    }

                                    if (checkIsExternalLink(item.linkTo)) {
                                        return (
                                            <MenuItemWrapper key={index} href={item.linkTo}>
                                                <MenuItemIcon className={item.icon} />
                                                <MenuItemText>{item.text}</MenuItemText>
                                            </MenuItemWrapper>
                                        );
                                    }
                                    return (
                                        <LinkedMenuItemWrapper
                                            onClick={menuItemClick}
                                            key={index}
                                            to={createSolutionRoute(item.linkTo)}
                                        >
                                            <MenuItemIcon className={item.icon} />
                                            <MenuItemText>{item.text}</MenuItemText>
                                        </LinkedMenuItemWrapper>
                                    );
                                })}
                        </MenuItemsWrapper>
                        <FlexSpace />
                        {headerData?.user?.isInternalUser && showImportButton && (
                            <ItemMenuWrapper
                                ref={modalRef}
                                isSelected={showCreateMenu}
                                tabIndex={0}
                                onKeyDown={handleKeyDownImport}
                            >
                                <ItemMenuButton onClick={handleOpenImportModal}>
                                    <ArrowRightIcon />
                                    <span>
                                        {
                                            headerData.translations
                                                .TI2020_ContentTranslations_Header_CreateMenuButton_Import
                                        }
                                    </span>
                                </ItemMenuButton>
                            </ItemMenuWrapper>
                        )}
                        <ItemMenuWrapper
                            ref={modalRef}
                            isSelected={showCreateMenu}
                            tabIndex={0}
                            onKeyDown={handleKeyDownCreate}
                        >
                            <ItemMenuButton onClick={() => onHandleOpenCloseCreateMenu()}>
                                <CreateMenuButtonIcon className="fas fa-plus" />
                                <span>
                                    {
                                        headerData.translations
                                            .TI2020_ContentTranslations_Header_CreateMenuButton_CreateNew
                                    }
                                </span>
                            </ItemMenuButton>
                        </ItemMenuWrapper>
                        <Suspense
                            fallback={
                                <DelayedFallback>
                                    <SuspendedCreateMenu
                                        handleOpenCloseCreateMenu={() => onHandleOpenCloseCreateMenu()}
                                        isOnTop={isOnTop}
                                    />
                                </DelayedFallback>
                            }
                        >
                            {showCreateMenu && (
                                <CreateMenu
                                    baseUrl={baseUrl}
                                    userData={headerData.user}
                                    isOnTop={isOnTop}
                                    showCreateMenu={true}
                                    closeCreateMenu={onHandleOpenCloseCreateMenu}
                                    translations={headerData.translations}
                                    clickSolutionItemHandler={clickSolutionItemHandler}
                                    solutionsData={solutionsData}
                                    onScrollToPortfolio={onScrollToPortfolio}
                                    handleFocusParent={() => modalRef.current?.focus()}
                                />
                            )}
                        </Suspense>
                        <PopperBase
                            trigger="click"
                            placement="bottom-start"
                            closeOnScroll
                            closeOnClickOutside
                            ref={userPopperRef as any}
                            popperElement={
                                <UserMenu
                                    popperRef={userPopperRef}
                                    baseUrl={baseUrl}
                                    items={headerData.user.menuItems}
                                    onClientSwitchContext={handleClientContextChange}
                                    licenseType={headerData.user.licenseType}
                                    clientName={headerData.user.clientName}
                                    translations={headerData.translations}
                                    onPurchaseLicense={redirectToPricingPage}
                                    onUpgradeLicense={showUpgradeLicenseContactUsModal}
                                    onSignOut={handleSignOut}
                                    handleFocusParent={handleFocusParent}
                                />
                            }
                        >
                            <UserMenuWrapper id="userMenuTrigger" tabIndex={0} onKeyDown={handleKeyDownUserMenu}>
                                <div
                                    style={{
                                        display: 'flex',
                                        height: '100%',
                                        alignItems: 'center',
                                    }}
                                    onClick={toggleMenu}
                                >
                                    <UserMenuText>
                                        <UserMenuUserName>
                                            {headerData.user.premiumSubscription.isEnabled &&
                                            headerData.user.licenseStatus === ELicenseStatus.ACTIVE ? (
                                                <PremiumIcon className="ti-icon-advanced-license-user-menu" />
                                            ) : null}
                                            {headerData.user.clientName !== null
                                                ? headerData.user.clientName
                                                : headerData.user.firstName}
                                        </UserMenuUserName>
                                    </UserMenuText>
                                    <UserMenuIcon>
                                        <span className="fas fa-chevron-down" />
                                    </UserMenuIcon>
                                </div>
                            </UserMenuWrapper>
                        </PopperBase>
                    </Fragment>
                ) : (
                    <HeaderSkeleton isOnTop={isOnTop} />
                )}
            </Wrapper>

            <>{fixedNotification && isOnTop && <NotificationsSubstrate isShow={isHasNotifications} />}</>

            <NotificationsWrapper
                isOnTop={isOnTop}
                className="ti-header-notifications"
                fixedNotification={fixedNotification}
                ref={notificationWrapperRef as LegacyRef<any>}
            >
                <NotificationsManager
                    dismissText={headerData?.translations?.TI2020_ContentTranslations_Header_Dismiss || 'DISMISS'}
                    onAdd={onAddNotifications}
                    onClose={onCloseNotification}
                />
            </NotificationsWrapper>
            <BetaSuggestionModal
                translations={headerData?.translations}
                clickSolutionItemHandler={clickSolutionItemHandler}
            />
            <ImportProject
                translations={headerData.translations}
                showModal={showImportModal}
                onClose={() => setShowImportModal(false)}
            />
        </Theme>
    );
};

Header.defaultProps = {
    options: {
        advancedFeatures: {
            renderIndications: true,
        },
    },
    isNewBrand: true,
};
